import { useMemo } from 'react';
import Modal from '@shared/components/atoms/Modal/Modal';
import { useVacationDetailQuery, useGetVacationFile } from '@features/vacation/hooks';

import { formatSimpleDate } from '@shared/utils/formatDate';
import safeToBase64Image from '@shared/lib/safeToBase64Image';
import LoadingOverlay from '@shared/components/atoms/LoadingOverlay/LoadingOverlay';
import { renderApproverSign } from '@shared/components/atoms/RenderApproverSign';

import styles from '@pages/vacation/components/VacationDetailModal/VacationDetailModal.module.scss';
// 휴가 상세 모달 컴포넌트
export default function VacationDetailModal({
  isOpen,
  onClose,
  onSuccess,
  vacationIdx,
  vacationStatus,
  onAlert,
}) {
  const { mutate: downloadFile, isPending: isDownloadingFile } = useGetVacationFile({
    onError: (err) => {
      if (typeof onAlert === 'function') {
        onAlert(
          err?.message || '보안 정책에 따라 첨부파일 다운로드는 허용된 IP 환경에서만 가능합니다.',
        );
      }
    },
  });

  const {
    data: vacationData,
    isLoading,
    isFetching,
    isError,
  } = useVacationDetailQuery(vacationIdx);

  const { writerBase64, signerBase64 } = useMemo(() => {
    if (!vacationData) {
      return {
        writerBase64: null,
        signerBase64: null,
      };
    }
    return {
      writerBase64: vacationData.writer_sign ? safeToBase64Image(vacationData.writer_sign) : null,

      signerBase64: vacationData.signer_sign ? safeToBase64Image(vacationData.signer_sign) : null,
    };
  }, [vacationData]);

  function handleFileClick(fileIdx, fileName, fileURL) {
    downloadFile({ fileIdx, fileName, fileURL });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={true}
      classes={{ modalBody: styles.vacationDetailModalBody }}
      closeOnOverlayClick={false}
    >
      {isLoading || isFetching ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingMessage}>문서를 불러오는 중...</div>
        </div>
      ) : !vacationData ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingMessage}>데이터가 없습니다.</div>
        </div>
      ) : (
        <>
          <div className={styles.modalBodyInner}>
            <div className={styles.scrollArea}>
              {vacationData.state == 7 && (
                <div className={styles.overlapWarning}>관리자 취소 처리 된 문서입니다.</div>
              )}
              <div className={styles.modalContent}>
                {isDownloadingFile && <LoadingOverlay message="파일 다운로드 중..." />}
                {/* 헤더영역 */}
                <div className={styles.headerContainer}>
                  <div className={styles.headerBox}>
                    <div className={styles.headerTitle}>휴가신청서</div>
                    <div className={styles.approverContainer}>
                      {/* 신청자 */}
                      <div className={styles.signerBox}>
                        <span className={styles.signLabel}>기안자</span>
                        <div className={styles.signContent}>
                          <img
                            src={writerBase64 || undefined}
                            width={38}
                            height={38}
                            alt="신청자 전자서명 "
                          />
                        </div>
                      </div>
                      {/* 결재자 */}
                      <div className={styles.signerBox}>
                        <span className={styles.signLabel}>결재자</span>
                        <div className={styles.signContent}>
                          {renderApproverSign(vacationData.state, signerBase64, styles)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* 바디 영역 */}
                <div className={styles.bodyContainer}>
                  <table className={styles.tableContainer}>
                    <tbody>
                      <tr>
                        <td>소속</td>
                        <td>{vacationData.writerInfo.team}</td>
                      </tr>
                      <tr>
                        <td>성명</td>
                        <td>{vacationData.writerInfo.name}</td>
                      </tr>
                      <tr>
                        <td>신청내역</td>
                        <td>
                          {vacationData.vacation_type.name || '-'}
                          {vacationData.cc_type?.name ? `- ${vacationData.cc_type.name}` : ''}
                        </td>
                      </tr>
                      <tr>
                        <td>휴가기간</td>
                        <td>
                          {formatSimpleDate(vacationData.start_date)} ~
                          {formatSimpleDate(vacationData.end_date)} ({vacationData.period}일)
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="2" className={styles.reason}>
                          <div className={styles.reasonContent}>{vacationData.reason}</div>
                        </td>
                      </tr>
                      <tr>
                        <td>첨부파일</td>
                        <td>
                          {Array.isArray(vacationData.fileList) && 0 < vacationData.fileList.length
                            ? vacationData.fileList.map((file, index) => (
                                <div key={index}>
                                  <button
                                    onClick={() =>
                                      handleFileClick(file.idx, file.name, file.url || file.fileURL)
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.fileLink}
                                  >
                                    {file.name}
                                  </button>
                                </div>
                              ))
                            : '없음'}
                        </td>
                      </tr>
                      <tr>
                        <td>참조자</td>
                        <td>
                          <div className={styles.recipientList}>
                            {Array.isArray(vacationData.recipientInfos) &&
                            vacationData.recipientInfos.length > 0
                              ? vacationData.recipientInfos.map((r) => r.name).join(', ')
                              : '없음'}
                          </div>
                        </td>
                      </tr>
                      {vacationData.reject_reason && (
                        <tr>
                          <td>코멘트</td>
                          <td>{vacationData.reject_reason}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className={styles.statusTableContainer}>
                    <table className={styles.statusTable}>
                      <thead>
                        <tr>
                          <th colSpan="3">발생분</th>
                          <th colSpan="2">사용분(반영)</th>
                          <th colSpan="2">잔여분</th>
                        </tr>
                        <tr>
                          <th>년초 발생분</th>
                          <th>
                            개정 후<br /> 발생분
                          </th>
                          <th>당해 추가분</th>
                          <th>
                            전년
                            <br /> 선사용분
                          </th>
                          <th>당해 사용분</th>
                          <th>개정 후</th>
                          <th className={styles.bold}>총 잔여분</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{vacationStatus.generated.beginningOfYear}</td>
                          <td>{vacationStatus.generated.afterRevision}</td>
                          <td>{vacationStatus.generated.currentYearAdditional}</td>
                          <td>{vacationStatus.used.previousYearPreUsed}</td>
                          <td>{vacationStatus.used.currentYearUsed}</td>
                          <td>{vacationStatus.remaining.afterRevision}</td>
                          <td className={styles.bold}>{vacationStatus.remaining.total}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className={styles.bottomContainer}>
                    <div className={styles.bottomBox}>
                      <div className={styles.bottomTitle}>위와 같이 휴가를 신청합니다.</div>
                      <div className={styles.bottomContent}>
                        {formatSimpleDate(vacationData.date, { style: 'korean' })}
                        <br />
                        신청인 : {vacationData.writerInfo.name}
                      </div>
                    </div>
                    <div className={styles.bottomCompany}>
                      (주) {vacationData.writerInfo.company}귀중
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
